import React, { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { Icon } from "@iconify/react";
import style from "../scss/RecordatoriosAgenda.module.scss";
import useAgenda from "../hooks/useAgenda";
import useCerrarAlClicFuera from "../hooks/useCerrarAlClicFuera";
import {
  compromisosPorAvisar,
  fechaHoyTexto,
  formatearHora,
  momentoCompromiso,
  recordatorio,
  resumenAvisos,
} from "../utils/agenda";
import {
  guardarPreferenciaNotificaciones,
  mostrarNotificacion,
  notificacionesActivas,
  permisoNotificaciones,
  solicitarPermisoNotificaciones,
} from "../utils/notificacionesNavegador";

const MAXIMO_EN_PANEL = 5;
const MINUTOS_ANTES = 30;
const MS_POR_MINUTO = 60 * 1000;
// un aviso "es ahora" todavía se muestra si la página abrió hasta 10 min tarde
const TOLERANCIA_AHORA = 10 * MS_POR_MINUTO;
// solo se programan avisos de las próximas 24 h (la página se recarga seguido)
const VENTANA_PROGRAMACION = 24 * 60 * MS_POR_MINUTO;

// Cada aviso se muestra una sola vez por pestaña (la app recarga la página al
// navegar). Si el navegador bloquea sessionStorage, se puede repetir.
const yaMostrado = (clave) => {
  try {
    return sessionStorage.getItem(clave) === "1";
  } catch {
    return false;
  }
};

const marcarMostrado = (clave) => {
  try {
    sessionStorage.setItem(clave, "1");
  } catch {
    // sin almacenamiento: no se recuerda el aviso
  }
};

// muestra el aviso solo si no se había mostrado (clave única por aviso)
const avisarUnaVez = (clave, avisar) => {
  if (yaMostrado(clave)) return;
  marcarMostrado(clave);
  avisar();
};

// Programa las notificaciones de los compromisos con hora: 30 min antes y a la
// hora. Devuelve los temporizadores para cancelarlos al desmontar.
const programarAvisosConHora = (compromisos) => {
  const ahora = Date.now();
  const temporizadores = [];

  compromisos
    .filter((c) => !c.cumplido)
    .forEach((compromiso) => {
      const momento = momentoCompromiso(compromiso)?.getTime();
      if (!momento) return;
      const hora = formatearHora(compromiso.hora);

      const avisos = [
        {
          clave: `agenda:notif:${compromiso.id}:${compromiso.fecha}:${compromiso.hora}:antes`,
          cuando: momento - MINUTOS_ANTES * MS_POR_MINUTO,
          limite: momento,
          titulo: `En ${MINUTOS_ANTES} minutos · ${hora}`,
        },
        {
          clave: `agenda:notif:${compromiso.id}:${compromiso.fecha}:${compromiso.hora}:ahora`,
          cuando: momento,
          limite: momento + TOLERANCIA_AHORA,
          titulo: `Es ahora · ${hora}`,
        },
      ];

      avisos.forEach(({ clave, cuando, limite, titulo }) => {
        if (ahora >= limite || cuando - ahora > VENTANA_PROGRAMACION) return;
        const notificar = () =>
          avisarUnaVez(clave, () => mostrarNotificacion(titulo, compromiso.descripcion, clave));
        temporizadores.push(setTimeout(notificar, Math.max(cuando - ahora, 0)));
      });
    });

  return temporizadores;
};

// Campana del encabezado con los compromisos de la agenda que requieren
// atención (vencidos, hoy, mañana y próximos días), aviso al entrar y
// notificaciones del navegador.
const RecordatoriosAgendaComponent = () => {
  const { compromisos, cargando } = useAgenda();
  const [abierto, setAbierto] = useState(false);
  const [permiso, setPermiso] = useState(permisoNotificaciones);
  const [notificar, setNotificar] = useState(notificacionesActivas);
  const contenedorRef = useRef(null);
  const porAvisar = compromisosPorAvisar(compromisos);

  // aviso al entrar (una vez por día): en la página y, si están activas, en el sistema
  useEffect(() => {
    if (cargando || porAvisar.length === 0) return;
    const resumen = resumenAvisos(porAvisar).join(" · ");

    avisarUnaVez(`agenda:avisado:${fechaHoyTexto()}`, () =>
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "info",
        title: "Recordatorio de tu agenda",
        text: resumen,
        showConfirmButton: true,
        confirmButtonText: "Ver agenda",
        showCloseButton: true,
        timer: 12000,
        timerProgressBar: true,
      }).then((result) => {
        if (result.isConfirmed) window.location.href = "/agenda";
      })
    );

    if (notificar) {
      avisarUnaVez(`agenda:notificado:${fechaHoyTexto()}`, () =>
        mostrarNotificacion("Recordatorio de tu agenda", resumen, "agenda-resumen")
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargando, notificar]);

  // notificaciones 30 min antes y a la hora de los compromisos con hora
  useEffect(() => {
    if (!notificar) return undefined;
    const temporizadores = programarAvisosConHora(compromisos);
    return () => temporizadores.forEach(clearTimeout);
  }, [compromisos, notificar]);

  // cierra el panel al hacer clic fuera o con Escape
  const cerrarPanel = useCallback(() => setAbierto(false), []);
  useCerrarAlClicFuera(contenedorRef, abierto, cerrarPanel);

  const activarNotificaciones = async () => {
    const concedido = await solicitarPermisoNotificaciones();
    setPermiso(permisoNotificaciones());
    setNotificar(concedido);
    if (concedido) {
      mostrarNotificacion("Notificaciones activadas", "Te avisaremos de los compromisos de tu agenda.", "agenda-activadas");
    }
  };

  const desactivarNotificaciones = () => {
    guardarPreferenciaNotificaciones(false);
    setNotificar(false);
  };

  const total = porAvisar.length;
  const etiqueta = total ? `Agenda: ${total} compromisos por atender` : "Agenda: sin compromisos por atender";

  // opción de notificaciones del navegador según el permiso actual
  const opcionNotificaciones = () => {
    if (permiso === "unsupported") return null;
    if (permiso === "denied") {
      return (
        <p className={style.panelNota}>
          <Icon icon="mdi:bell-off-outline" aria-hidden="true" /> Las notificaciones están bloqueadas en este
          navegador. Actívalas desde el candado de la barra de direcciones.
        </p>
      );
    }
    return notificar ? (
      <button type="button" className={style.panelOpcion} onClick={desactivarNotificaciones}>
        <Icon icon="mdi:bell-check-outline" aria-hidden="true" /> Notificaciones del navegador activas · Desactivar
      </button>
    ) : (
      <button type="button" className={`${style.panelOpcion} ${style.panelOpcionDestacada}`} onClick={activarNotificaciones}>
        <Icon icon="mdi:bell-plus-outline" aria-hidden="true" /> Activar notificaciones del navegador
      </button>
    );
  };

  return (
    <div className={style.contenedor} ref={contenedorRef}>
      <button
        type="button"
        className={`${style.campana} ${total ? style.campanaActiva : ""}`}
        onClick={() => setAbierto(!abierto)}
        aria-label={etiqueta}
        aria-expanded={abierto}
        title={etiqueta}
      >
        <Icon icon={total ? "mdi:bell-ring-outline" : "mdi:bell-outline"} aria-hidden="true" />
        {total > 0 && <span className={style.contador}>{total > 9 ? "9+" : total}</span>}
      </button>

      {abierto && (
        <div className={style.panel} role="dialog" aria-label="Recordatorios de la agenda">
          <p className={style.panelTitulo}>Recordatorios</p>
          {total === 0 ? (
            <p className={style.panelVacio}>
              <Icon icon="mdi:check-circle-outline" aria-hidden="true" /> No tienes compromisos próximos.
            </p>
          ) : (
            <ul className={style.panelLista}>
              {porAvisar.slice(0, MAXIMO_EN_PANEL).map((compromiso) => {
                const aviso = recordatorio(compromiso);
                return (
                  <li key={compromiso.id} className={`${style.panelItem} ${style[aviso.tipo]}`}>
                    <span className={style.panelAviso}>{aviso.texto}</span>
                    <span className={style.panelDescripcion}>{compromiso.descripcion}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {opcionNotificaciones()}
          <a href="/agenda" className={style.panelEnlace}>
            {total > MAXIMO_EN_PANEL ? `Ver los ${total} en la agenda` : "Ir a la agenda"}
            <Icon icon="mdi:arrow-right" aria-hidden="true" />
          </a>
        </div>
      )}
    </div>
  );
};

export default RecordatoriosAgendaComponent;
